import { parseSpeakerLabelIndex, RAW_SPEAKER_LABEL_SOURCE } from "@lexicanon/shared"
import type { IHighlightRange, ISnippetSegment, IWindowedSnippet } from "./interfaces"

/** A fresh matcher each call — a shared `/g` regex would keep `lastIndex` between hits. */
const rawLabelInText = (): RegExp => new RegExp(`\\b${RAW_SPEAKER_LABEL_SOURCE}\\b`, "gi")

/**
 * Pure snippet helpers for {@link SearchHitCard} — speaker-label display,
 * query-term highlighting and match-aware windowing. Kept free of React/i18n
 * so they unit-test as plain string functions.
 */

/**
 * Turn a bare diarisation label into the words people read.
 * `Speaker_3`, `Speaker_S3`, `SPEAKER_03` and `Remote::Speaker_2` become
 * "Speaker 3" / "Speaker 2". A real name is returned unchanged.
 */
export const formatUnresolvedSpeakerLabel = (label: string): string => {
  const index = parseSpeakerLabelIndex(label)
  if (index === null) return label
  return `Speaker ${index}`
}

/**
 * Snippet text for a hit. Raw labels inside the text become "Speaker N".
 * When the hit already shows `speaker` on its own line, a leading
 * "Name:" prefix that repeats that speaker is removed from each line so
 * the name is not printed twice.
 */
export const prepareSnippetContent = (content: string, speaker: string | null): string => {
  const humanised = content.replace(rawLabelInText(), (match) => formatUnresolvedSpeakerLabel(match))
  const display = speaker !== null && speaker.trim().length > 0 ? formatUnresolvedSpeakerLabel(speaker).trim() : null
  if (display === null) return humanised

  const prefix = `${display}:`
  return humanised
    .split("\n")
    .map((line) => {
      const trimmedStart = line.trimStart()
      if (!trimmedStart.startsWith(prefix)) return line
      const lead = line.slice(0, line.length - trimmedStart.length)
      return lead + trimmedStart.slice(prefix.length).trimStart()
    })
    .join("\n")
}

/** Combining diacritical marks — stripped after NFD so café == cafe. */
const COMBINING_MARKS = /[\u0300-\u036f]/g

/** Query tokens shorter than this (after folding) are ignored as too noisy. */
const MIN_TOKEN_LENGTH = 2

/**
 * ~3 lines of snippet text. Content at or below this renders whole; a first
 * match beyond it gets windowed so it lands inside the CSS clamp.
 */
const VISIBLE_BUDGET = 160

/** Characters of lead-in context kept before the first match when windowing. */
const LEAD_CONTEXT = 28

interface IFolded {
  folded: string
  /**
   * For each folded-string index, the source `[start, end)` UTF-16 span that
   * produced it, so a match found in the folded string maps back to exact
   * indices in the original (which is what we slice for rendering).
   */
  map: { start: number; end: number }[]
}

/**
 * Case- and diacritic-fold `input` while preserving a per-character map back to
 * the source. Folding is done per code point (NFD → drop combining marks →
 * lowercase) so index math survives precomposed accents and surrogate pairs.
 */
const foldForSearch = (input: string): IFolded => {
  const chars: string[] = []
  const map: { start: number; end: number }[] = []
  let unit = 0

  for (const cp of input) {
    const span = { start: unit, end: unit + cp.length }
    const folded = cp.normalize("NFD").replace(COMBINING_MARKS, "").toLowerCase()
    for (const ch of folded) {
      chars.push(ch)
      map.push(span)
    }
    unit += cp.length
  }

  return { folded: chars.join(""), map }
}

/** Sort by start then merge overlapping AND touching ranges into one list. */
const mergeRanges = (ranges: IHighlightRange[]): IHighlightRange[] => {
  if (ranges.length <= 1) return ranges.map((r) => ({ ...r }))

  const sorted = [...ranges].sort((a, b) => a.start - b.start || a.end - b.end)
  const merged: IHighlightRange[] = [{ ...sorted[0] }]

  for (let i = 1; i < sorted.length; i++) {
    const last = merged[merged.length - 1]
    const cur = sorted[i]
    if (cur.start <= last.end) {
      last.end = Math.max(last.end, cur.end)
    } else {
      merged.push({ ...cur })
    }
  }

  return merged
}

/**
 * Compute the highlight ranges for `query`'s tokens inside `content`.
 * Case- and diacritic-insensitive; tokens under {@link MIN_TOKEN_LENGTH} chars
 * are dropped; overlapping/adjacent hits are merged. Returns `[]` when there is
 * nothing to highlight (empty input or a purely semantic match).
 */
export const buildHighlightRanges = (content: string, query: string): IHighlightRange[] => {
  if (content.length === 0 || query.length === 0) return []

  const { folded, map } = foldForSearch(content)
  const foldedQuery = foldForSearch(query).folded
  const tokens = Array.from(
    new Set(foldedQuery.split(/[^\p{L}\p{N}]+/u).filter((token) => token.length >= MIN_TOKEN_LENGTH)),
  )
  if (tokens.length === 0) return []

  const ranges: IHighlightRange[] = []
  for (const token of tokens) {
    let from = folded.indexOf(token)
    while (from !== -1) {
      const to = from + token.length
      ranges.push({ start: map[from].start, end: map[to - 1].end })
      from = folded.indexOf(token, from + 1)
    }
  }

  return mergeRanges(ranges)
}

/**
 * Window `content` around its first highlight so the match stays inside the
 * 3-line clamp. Short content (or a match already near the top) passes through
 * unchanged. Otherwise the excerpt starts a little before the first match,
 * snapped to a word boundary, flagged for a leading "…"; `ranges` are re-based
 * to index into the returned `text`.
 */
export const windowSnippet = (content: string, ranges: IHighlightRange[]): IWindowedSnippet => {
  const firstStart = ranges.length > 0 ? ranges[0].start : null

  if (content.length <= VISIBLE_BUDGET || firstStart === null || firstStart <= VISIBLE_BUDGET) {
    return { text: content, leadingEllipsis: false, ranges }
  }

  let start = firstStart - LEAD_CONTEXT
  const space = content.indexOf(" ", start)
  if (space !== -1 && space < firstStart) start = space + 1
  else start = Math.max(0, start)

  const rebased = ranges
    .map((r) => ({ start: r.start - start, end: r.end - start }))
    .filter((r) => r.end > 0)
    .map((r) => ({ start: Math.max(0, r.start), end: r.end }))

  return { text: content.slice(start), leadingEllipsis: start > 0, ranges: rebased }
}

/**
 * Split `text` into alternating plain/highlighted segments from a sorted, merged
 * `ranges` list. Ranges are clamped to the text bounds defensively so a stale
 * index can never throw. Zero-length pieces are skipped.
 */
export const toSegments = (text: string, ranges: IHighlightRange[]): ISnippetSegment[] => {
  if (ranges.length === 0) return text.length > 0 ? [{ text, highlight: false }] : []

  const segments: ISnippetSegment[] = []
  let cursor = 0

  for (const range of ranges) {
    const start = Math.max(0, Math.min(range.start, text.length))
    const end = Math.max(start, Math.min(range.end, text.length))
    if (start > cursor) segments.push({ text: text.slice(cursor, start), highlight: false })
    if (end > start) segments.push({ text: text.slice(start, end), highlight: true })
    cursor = Math.max(cursor, end)
  }

  if (cursor < text.length) segments.push({ text: text.slice(cursor), highlight: false })
  return segments
}
